import Header from "../component/Header";
import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, FormControl, Grid, MenuItem, Select, TextField, Typography, Backdrop, CircularProgress} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AlertsDialog from "../component/AlertsDialog";
import AlertsSnackbar from "../component/AlertsSnackbar";
import { useCookies } from "react-cookie";
import MyBottomNavigation from "../component/MyBottomNavigation";
import { getCategoriesApi, getPlacesApi, setRecoveryHistoryApi } from "../services/api/api";
import { useRecoilState } from "recoil";
import { CurrentPosition } from "../store/currentState";
import { permittedRadius } from "../constants/constants";
import { MakerData } from "../types/type";
import RequiredBox from "../component/RequiredBox";
import { getCurrentPosition, sortMarkersAtCenter } from "../hooks/useFunction";
import Webcam from "react-webcam";
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { useTranslation } from "react-i18next";

function Checkin(): JSX.Element {
  const { t } = useTranslation();
  const [cookies, setCookie, removeCookie] = useCookies(['recotech_mapapps_token']);
  const [isWaitingResponse, setIsWaitingResponse] = useState<boolean>(false);
  const [markers, setMarkers] = useState<MakerData[]>([]);
  const [marker, setMarker] = useState<MakerData | null>(null);
  const [categoryAll, setCategoryAll] = useState<any[]>([]);
  const [totalWeight, setTotalWeight] = useState<string>("0");

  const navigate = useNavigate();

  const [currentPosition, setCurrentPosition] = useRecoilState(CurrentPosition);

  const [files, setFiles] = useState<File[]>([]);
  
  const webcamRef = useRef<Webcam>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [isCameraPermission, setIsCameraPermission] = useState<boolean>(true);

  const capture = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      setImageSrc(imageSrc);
      fetch(imageSrc).then(res => res.blob()).then(blob => {
        const file = new File([blob], "captured.jpg", { type: 'image/jpeg' });
        setFiles([file]);
      });
    } else {
      setImageSrc(null);
      setFiles([]); // ファイルをクリア
    }
  }, [webcamRef, setFiles]);


  interface CategoryData {
    id: string;
    name: string;
    amount: string;
    unit: string;
  }
  const [categoryDatum, setCategoryDatum] = useState<CategoryData[]>([
    { id: '', name: '', amount: '', unit: ''},
  ]);

  const handleInputChange = (index: number, field: keyof CategoryData, value: string) => {
    const newcategoryDatum = [...categoryDatum];
    const matchingCategory = categoryAll.find(category => category.name === value);
    if (matchingCategory) {
        newcategoryDatum[index] = {
            ...newcategoryDatum[index],
            [field]: value,
            id: matchingCategory.id, // Update the id
            unit: matchingCategory.category_unit, // Update the unit
        };
    } else {
        newcategoryDatum[index] = {
            ...newcategoryDatum[index],
            [field]: value,
        };
    }
    calculateTotalWeight(newcategoryDatum);
    setCategoryDatum(newcategoryDatum);
};

  const handleAddItem = () => {
    //もし既に全てのカテゴリーが選択されていたらreturn
    const markerCategoryNames = marker?.categoryNames || [];
    const categoryDatumNamesSet = new Set(categoryDatum.map(item => item.name));
    if (markerCategoryNames.every(categoryName => categoryDatumNamesSet.has(categoryName))) {
      return;
    }
    // categoryDatumに空白がある場合はreturn
    for (let index = 0; index < categoryDatum.length; index++) {
      const item = categoryDatum[index];    
      if (item.name === "" || item.amount === "") {
        return;
      }
    }
    setCategoryDatum([...categoryDatum, {id:'', name: '', amount: '', unit: '' }]);
  };

  const handleRemoveItem = () => {
    if (categoryDatum.length > 1) {
      const newCategoryData = [...categoryDatum];
      newCategoryData.pop(); // 配列から最後の要素を削除
      setCategoryDatum(newCategoryData);
      calculateTotalWeight(newCategoryData);
    }
  };

  const handleSendClick = (() => {
    for (let index = 0; index < categoryDatum.length; index++) {
      const item = categoryDatum[index];
    
      if (item.name === "") {
        setAlertsMessage(t('pages.checkin.itemEmpty'));
        setSeverity("error");
        setOpenSnackbar(true);
        return;
      }
    
      if (item.amount === "") {
        setAlertsMessage(t('pages.checkin.amountEmpty'));
        setSeverity("error");
        setOpenSnackbar(true);
        return;
      }
    }
    
    if(files!.length === 0){
      setAlertsMessage(t('pages.checkin.photoRequired'))
      setSeverity("error")
      setOpenSnackbar(true)
      return
    }
    setOpenDialog(true)
  })

  const calculateTotalWeight = (categoryDatum:any[]) => {
    let totalWeight = 0;
    categoryDatum.forEach(item => {
      const categoryDetail = categoryAll.find(category => category.name === item.name);
        if (categoryDetail) {
            totalWeight += item.amount * categoryDetail.amount_per_unit;
        }
    });
    setTotalWeight(totalWeight.toFixed(3).toString());
  };

  //SnackBarの設定
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [alertsMessage, setAlertsMessage] = useState<string>("");
  const [severity, setSeverity] = useState< "error" | "success" >("error");
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  //api情報を読み込みセットする
  const fetchData = async () => {
    if (!cookies.recotech_mapapps_token) navigate("/");
    setIsWaitingResponse(true)
    const position = await getCurrentPosition()
    setCurrentPosition(position)
    const placesBounds = await getPlacesApi(100,position.lat, position.lng,permittedRadius,setAlertsMessage,setSeverity,setOpenSnackbar)
    const categoryAll = await getCategoriesApi(setAlertsMessage,setSeverity,setOpenSnackbar);
    setCategoryAll(categoryAll)

    if(placesBounds.length === 0){
      setAlertsMessage(t('pages.checkin.noPointNearby'))
      setSeverity("error")
      setOpenSnackbar(true)
      setIsWaitingResponse(false)
      return
    }else{
      //近い順に並び替えを行う
      sortMarkersAtCenter(placesBounds,currentPosition)
      setMarkers(placesBounds)
      setMarker(placesBounds[0])
      setIsWaitingResponse(false)
    }
  };

  useEffect(() => {
    fetchData();
    // メモリリークを防ぐため、データURIを必ず失効させること、アンマウント時に実行すること
    return () => files.forEach(file => URL.revokeObjectURL(URL.createObjectURL(file)));
  }, []);

  const handleAgreeClick = (async () => {
    setIsWaitingResponse(true)
    const position = await getCurrentPosition()
    const formData = new FormData();    
    formData.append("recovery_history[recovery_place_id]", marker?.id?.toString()??"");
    formData.append("recovery_history[latitude]", position.lat?.toString()??"");
    formData.append("recovery_history[longitude]", position.lng?.toString()??"");
    formData.append("recovery_history[image]", files[0]??"");
    if(categoryDatum.length > 0){
      categoryDatum?.forEach((categoryData:CategoryData) => {
        formData.append("recovery_history[category_ids][]", categoryData.id);
        formData.append("recovery_history[category_amounts][]", categoryData.amount);
      });
    }
    const res = await setRecoveryHistoryApi(cookies.recotech_mapapps_token,formData,setAlertsMessage,setSeverity,setOpenSnackbar,setIsWaitingResponse)
    if(res){
      //登録後の処理
      setTimeout(() => {
        navigate("/wallet");
      },3000); //アラートを表示するために3秒待つ
    }else{
      //登録失敗の処理
    }
    setOpenDialog(false)
  })
  return (
    <>
      {isWaitingResponse ?
        <Backdrop open={isWaitingResponse} sx={{zIndex:10000}}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          width="100%"
        >
          <CircularProgress color='inherit' />
          <Typography component="div" variant="caption" sx={{color:"white",fontWeight:"bold"}}>
            {t('pages.checkin.sending')}
          </Typography>
        </Box>
      </Backdrop>
      :<></>
      }
      <AlertsSnackbar
        openSnackbar={openSnackbar}
        setOpenSnackbar={setOpenSnackbar}
        vertical={"top"}
        severity={severity}
        message={alertsMessage}
      />
      <AlertsDialog
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        handleAgreeClick={()=>handleAgreeClick()}
        dialogTitle={t('pages.checkin.title')}
        dialogMessage={t('pages.checkin.confirmRegister')}
        isWaitingResponse={isWaitingResponse}
      />
      <Header/>
        <Box 
          sx={{ p: 3 }}
          display="flex"
          alignItems="start"
          justifyContent="start"
          flexDirection="column"
        >
          {marker?
          <>
          <Typography component="div" variant="caption">
            {t('pages.checkin.instruction')}
          </Typography>
          <Box mt={2} display={"flex"} flexDirection={"row"} alignItems={"center"} justifyContent={"start"} >
            <Typography component="div" variant="h6" sx={{fontWeight:"bold"}}>
              {t('pages.checkin.pointName')}
            </Typography>
          </Box>
          <Select
            value={marker.name}
            onChange={(e) => {
              setMarker(markers.find(marker => marker.name === e.target.value)!)
              setCategoryDatum([{ id: '', name: '', amount: '', unit: ''}])
              setTotalWeight("0")
            }}
            style={{width: "85vw", marginTop: "10px"}}
            variant="outlined"
            fullWidth={true}
          >
            {markers.map((marker:MakerData,index:number) => (
              marker.name && <MenuItem key={index} value={marker.name}>
                {marker.name}
              </MenuItem>
            ))}
          </Select>        
          <Box mt={2} display={"flex"} flexDirection={"row"} alignItems={"center"} justifyContent={"start"} >
            <Typography component="div" variant="h6" sx={{fontWeight:"bold"}}>
              {t('pages.checkin.items')}
            </Typography>
            <RequiredBox/>
          </Box>
          {categoryDatum.map((item:CategoryData, index) => (
            <Grid container spacing={2} key={index} sx={{mb:1}}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <Select
                    id={`category-${index}`}
                    value={item.name}
                    onChange={(e) => handleInputChange(index, 'name', e.target.value)}
                  >
                    {marker.categoryNames.map((categoryName:string, index) => (
                      <MenuItem key={index} value={categoryName} disabled={categoryDatum.some(item => item.name === categoryName)}>
                        {categoryName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={3}>
                <FormControl fullWidth>
                  <TextField
                    id={`amount-${index}`}
                    type="number"
                    value={item.amount}
                    onChange={(e) => handleInputChange(index, 'amount', e.target.value)}
                  />
                </FormControl>
              </Grid>
              <Grid container item xs={3} alignItems="center">
                <Typography component="div" variant="body1">
                  {item.unit}
                </Typography>
              </Grid>

            </Grid>
          ))}
          <Grid container spacing={2} key={"addremove"} sx={{mb:1}}>
            <Grid item xs={6}>
              <Button 
                variant="contained"
                style={{width: "40vw",backgroundColor: "#4B4B4B", color: "white",borderRadius: "20px"}}
                onClick={handleAddItem}
              >
                {t('pages.checkin.addItem')}
              </Button>
            </Grid>
            <Grid item xs={6}>
            <Button 
                variant="contained"
                style={{width: "40vw",backgroundColor: "#4B4B4B", color: "white",borderRadius: "20px"}}
                onClick={handleRemoveItem}
              >
                {t('pages.checkin.deleteItem')}
              </Button>
            </Grid>
          </Grid>
          <Box
            width="85vw"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"  
          >
            <Box display="flex" justifyContent="center" alignItems="baseline" margin={1}>
              <Typography component="div" variant="body2">{t('pages.checkin.total')}</Typography>
              <Typography component="div" variant="h3">{`${totalWeight}`}</Typography>
              <Typography component="div" variant="body2">{t('pages.checkin.kg')}</Typography>
            </Box>
          </Box>
          <Box mt={2} display={"flex"} flexDirection={"row"} alignItems={"center"} justifyContent={"start"} >
            <Typography component="div" variant="h6" sx={{fontWeight:"bold"}}>
              {t('pages.checkin.photo')}
            </Typography>
            <RequiredBox/>
          </Box>
          <Box
            width="85vw"
            marginTop="10px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
          >
            {imageSrc && marker ? (
              <Box margin="10px">
                <img src={imageSrc} width={300} />
              </Box>
            ) : isCameraPermission ? (
              <Webcam
                audio={false}
                height={300}
                width={300}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                videoConstraints={{
                  height: 300,
                  width: 300,
                  facingMode: "environment" // 背面カメラを指定
                }}
                onUserMediaError={(error) => {
                  console.error("Webcam error:", error);
                  setIsCameraPermission(false);
                }}              
              />
            ) : (
              <Box margin="10px">
                <Typography variant="body1" color="error">
                  {t('pages.checkin.cameraPermission')}
                </Typography>
              </Box>
            )}
            <Button 
              variant="contained"
              style={{width: "40vw",backgroundColor: "#4B4B4B", color: "white",borderRadius: "20px",marginTop: "10px"}}
              onClick={capture}
              startIcon={<CameraAltIcon />}
            >
              {imageSrc ? t('pages.checkin.retake') : t('pages.checkin.takePhoto')}
            </Button>
          </Box>
          <Button
            variant="contained"
            style={{width: "85vw", marginTop: "30px" ,backgroundColor: "#4B4B4B", color: "white",borderRadius: "20px"}}
            onClick={() => {
              handleSendClick()
            }}
          >
            {t('pages.checkin.register')}
          </Button>
          <Box mt={10}></Box>
          </>
          :
          <>
          <Typography component="div" variant="caption">
            {t('pages.checkin.reloadInstruction')}
          </Typography>
          </>
          }
        </Box>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          width="100%"
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
          }}
        >
          <MyBottomNavigation />
        </Box>
    </>
  );
};
export default Checkin;
